const hashPattern = /^sha256-[A-Za-z0-9_-]{43}$/u;
const editorialVersionPattern = /^[a-z0-9][a-z0-9-]{0,63}$/u;

function escape(text) {
  return String(text).replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

const dateFormatters = {
  "en-US": new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" }),
  "de-DE": new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeZone: "UTC" }),
};

function shortVersion(revision) {
  return revision.contentHash ? revision.version : revision.version.slice(7, 19);
}

function readHref(manifest, revision) {
  return revision.href.startsWith("/api/")
    ? `${manifest.canonicalPath}?version=${revision.version}`
    : revision.href;
}

function revisionInfo(manifest, revision) {
  const de = manifest.locale === "de-DE";
  const editorial = manifest.kind === "editorial";
  const status = editorial
    ? (revision.version === manifest.defaultVersion ? (de ? "Aktuell" : "Current") : (de ? "Veröffentlicht" : "Published"))
    : revision.status === "draft"
      ? (de ? "Entwurf · nicht gültig" : "Draft · not effective")
      : revision.version === manifest.effectiveVersion
        ? (de ? "Gültig" : "Effective")
        : (de ? "Veröffentlicht" : "Published");
  const date = editorial ? revision.recordedAt : revision.effectiveDate ?? revision.recordedAt;
  return `<span class="badge badge-sm badge-ghost">${status}</span> <time datetime="${escape(date)}">${escape(dateFormatters[manifest.locale].format(new Date(date)))}</time> <code class="break-all text-xs">${escape(shortVersion(revision))}</code>`;
}

function renderHistoryDescription(manifest, selected) {
  const de = manifest.locale === "de-DE";
  if (!selected) return de ? "Wählen Sie eine Fassung zum Lesen." : "Choose a version to read.";
  return de
    ? `Lesen Sie eine Fassung oder vergleichen Sie sie mit <code class="break-all text-xs">${escape(shortVersion(selected))}</code>.`
    : `Read a version or compare it with <code class="break-all text-xs">${escape(shortVersion(selected))}</code>.`;
}

function renderRevisionRows(manifest, selected) {
  const de = manifest.locale === "de-DE";
  return manifest.revisions.map((revision) => {
    const current = selected?.version === revision.version;
    const compareHref = selected && !current
      ? `${manifest.canonicalPath}?version=${selected.version}&compare=${revision.version}`
      : null;
    const readLabel = `${de ? "Lesen" : "Read"}: ${revision.label}`;
    const compareLabel = de ? `${revision.label} mit der ausgewählten Fassung vergleichen` : `Compare ${revision.label} with the selected version`;
    return `<li class="list-row grid-cols-1 items-center gap-3 px-0 py-4 sm:grid-cols-[minmax(0,1fr)_auto]">
  <div class="flex min-w-0 flex-wrap items-center gap-2 text-sm">${revisionInfo(manifest, revision)}${current ? ` <span class="badge badge-sm badge-outline">${de ? "Ausgewählt" : "Selected"}</span>` : ""}</div>
  <div class="join justify-self-end">
    <a class="btn btn-sm join-item min-w-20" href="${escape(readHref(manifest, revision))}" aria-label="${escape(readLabel)}" data-fishy-tooltip="${escape(readLabel)}"${current ? ' aria-current="page"' : ""}>${de ? "Lesen" : "Read"}</a>
    ${compareHref ? `<a class="btn btn-sm join-item min-w-20" href="${escape(compareHref)}" aria-label="${escape(compareLabel)}" data-fishy-tooltip="${escape(compareLabel)}">${de ? "Vergleichen" : "Compare"}</a>` : ""}
  </div>
</li>`;
  }).join("\n");
}

function renderComparisonState(manifest, selected, baseline, view = "diff") {
  if (!selected || !baseline) return "";
  const de = manifest.locale === "de-DE";
  const controls = [
    ["left", de ? "Links" : "Left", de ? "Vergleichsfassung ohne Änderungsmarkierungen lesen" : "Read the comparison version without change highlights"],
    ["diff", "Diff", de ? "Änderungen zwischen beiden Fassungen anzeigen" : "Show changes between both versions"],
    ["right", de ? "Rechts" : "Right", de ? "Ausgewählte Fassung ohne Änderungsmarkierungen lesen" : "Read the selected version without change highlights"],
  ].map(([mode, label, description]) => {
    const href = `${manifest.canonicalPath}?version=${selected.version}&compare=${baseline.version}${mode === "diff" ? "" : `&view=${mode}`}`;
    return `<a class="tab${view === mode ? " tab-active" : ""}" href="${escape(href)}" data-fishy-tooltip="${escape(description)}"${view === mode ? ' aria-current="page"' : ""}>${label}</a>`;
  }).join("");
  return `<nav class="tabs tabs-border tabs-sm" aria-label="${de ? "Vergleichsansicht" : "Comparison view"}">${controls}</nav>
<a class="link text-sm sm:ml-auto" href="${escape(readHref(manifest, selected))}" data-fishy-tooltip="${de ? "Vergleich schließen und die ausgewählte Fassung lesen" : "Close the comparison and read the selected version"}">${de ? "Vergleich beenden" : "Exit comparison"}</a>`;
}

export function renderContentHistoryNavigation(manifest, selection = contentHistorySelection(manifest, "")) {
  const de = manifest.locale === "de-DE";
  const { selected, baseline, view = baseline ? "diff" : "document" } = selection;
  return `<aside class="contents not-prose" data-content-history aria-label="${de ? "Dokumentfassungen" : "Document versions"}">
  <button type="button" class="btn btn-sm btn-ghost fixed right-0 top-[calc(var(--fishy-page-outline-top)-3rem)] z-30 flex h-11 w-11 rounded-l-lg rounded-r-none border-0 px-2 py-2 text-base-content/45 shadow-none hover:bg-base-200/80 hover:text-base-content focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary [&[hidden]]:hidden print:hidden" aria-label="${de ? "Versionsverlauf" : "History"}" data-fishy-tooltip="${de ? "Versionsverlauf" : "History"}" commandfor="content-history-dialog" command="show-modal" data-content-history-open${manifest.revisions.length ? "" : " hidden"}>
    <svg class="size-5" aria-hidden="true"><use href="/img/icons.svg#fishy-history-2-fill"></use></svg>
  </button>
  <div class="mx-auto flex w-full min-w-0 max-w-prose flex-wrap items-center gap-3 border-y border-base-300 py-4 [&[hidden]]:hidden" data-content-comparison-state${selected && baseline ? "" : " hidden"}>${renderComparisonState(manifest, selected, baseline, view)}</div>
  <dialog id="content-history-dialog" class="modal" aria-labelledby="content-history-title" aria-describedby="content-history-description" data-content-history-panel>
    <div class="modal-box w-11/12 max-w-2xl">
      <h2 id="content-history-title" class="text-lg font-semibold">${de ? "Versionsverlauf" : "Version history"}</h2>
      <p id="content-history-description" class="mb-2 mt-1 text-sm leading-6">${renderHistoryDescription(manifest, selected)}</p>
      <ul class="list" data-content-history-revisions>${renderRevisionRows(manifest, selected)}</ul>
      <div class="modal-action flex-wrap items-center justify-between gap-3 border-t border-base-300 pt-4">
        <div class="flex flex-wrap gap-x-4 gap-y-2 text-sm">
          <a class="link" href="${escape(manifest.canonicalPath)}" data-fishy-tooltip="${de ? "Die Standardfassung dieses Dokuments öffnen" : "Open this document’s default version"}">${de ? "Standardfassung lesen" : "Read default version"}</a>
          <a class="link" data-content-selected-download data-fishy-tooltip="${de ? "Die ausgewählte Fassung als Klartext herunterladen" : "Download the selected version as plain text"}"${selected ? ` href="${escape(selected.textHref)}"` : " hidden"}>${de ? "Klartext herunterladen" : "Download plain text"}</a>
        </div>
        <form method="dialog" class="ml-auto"><button class="btn btn-sm" autofocus>${de ? "Schließen" : "Close"}</button></form>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop"><button>${de ? "Schließen" : "Close"}</button></form>
  </dialog>
  <p class="mx-auto my-0 w-full max-w-prose text-sm leading-6 empty:hidden" role="status" data-content-history-status></p>
</aside>`;
}

export function decodeContentHistory(value, { locale, canonicalPath, privateNotice = false }) {
  const editorial = value?.schemaVersion === 3 && value.kind === "editorial";
  const legal = value?.schemaVersion === 2 && value.kind === undefined;
  if ((!legal && !editorial) || (editorial && (privateNotice || typeof value.documentId !== "string" || !value.documentId.trim()))
    || !Object.hasOwn(dateFormatters, locale) || value.locale !== locale || value.canonicalPath !== canonicalPath
    || typeof canonicalPath !== "string" || !canonicalPath.startsWith("/") || canonicalPath.startsWith("//") || !canonicalPath.endsWith("/")
    || !Array.isArray(value.revisions) || value.revisions.length > 512) {
    throw new TypeError("Content history is invalid or unsupported");
  }
  const versions = new Set();
  for (const revision of value.revisions) {
    if (!revision || typeof revision.version !== "string" || !(editorial ? editorialVersionPattern : hashPattern).test(revision.version)
      || versions.has(revision.version)
      || (editorial ? revision.status !== "published" || !hashPattern.test(revision.contentHash) || revision.effectiveDate !== null
        : !["draft", "published"].includes(revision.status) || revision.contentHash !== undefined
          || (revision.status === "draft" ? revision.effectiveDate !== null : !/^\d{4}-\d{2}-\d{2}$/.test(revision.effectiveDate)))
      || typeof revision.label !== "string" || revision.label.length > 256
      || typeof revision.recordedAt !== "string" || !Number.isFinite(Date.parse(revision.recordedAt))) {
      throw new TypeError("Content history revision is invalid");
    }
    versions.add(revision.version);
    for (const field of ["href", "textHref"]) {
      const href = revision[field];
      if (typeof href !== "string" || !href.startsWith("/") || href.startsWith("//")) throw new TypeError("Content history URL is invalid");
      const url = new URL(href, "https://content.invalid");
      if (url.origin !== "https://content.invalid" || url.hash) throw new TypeError("Content history URL is invalid");
      if (privateNotice) {
        if (url.pathname !== "/api/v1/account/desktop-privacy" || url.searchParams.get("version") !== revision.version
          || url.searchParams.get("locale") !== locale || url.searchParams.getAll("version").length !== 1
          || url.searchParams.getAll("locale").length !== 1
          || url.searchParams.size !== 3
          || url.searchParams.getAll("format").length !== 1
          || url.searchParams.get("format") !== ({ href: "html", textHref: "text" })[field]) {
          throw new TypeError("Private legal history URL is invalid");
        }
      } else {
        const suffix = { href: "", textHref: "notice.txt" }[field];
        if (href !== `${canonicalPath}versions/${revision.version}/${suffix}` || url.pathname !== href || url.search) {
          throw new TypeError("Content history URL is outside its document");
        }
      }
    }
  }
  for (const field of ["effectiveVersion", "defaultVersion"]) {
    if (value[field] !== null && !versions.has(value[field])) throw new TypeError("Content history selection is missing");
  }
  if (editorial && value.effectiveVersion !== null) throw new TypeError("Editorial content cannot be legally effective");
  if (legal && value.effectiveVersion !== null && value.revisions.find(({ version }) => version === value.effectiveVersion)?.status !== "published") {
    throw new TypeError("A draft cannot be effective");
  }
  return value;
}

export function contentHistorySelection(manifest, search, initialVersion = manifest.defaultVersion) {
  const params = new URLSearchParams(search);
  if (["version", "compare", "view"].some((key) => params.getAll(key).length > 1)) throw new TypeError("Content version selection is ambiguous");
  const version = params.get("version") || initialVersion;
  const compare = params.get("compare") || null;
  const selected = manifest.revisions.find((revision) => revision.version === version);
  const baseline = compare && manifest.revisions.find((revision) => revision.version === compare);
  if ((version && !selected) || (compare && !baseline)) throw new TypeError("Requested content version is unavailable");
  const view = params.get("view") ?? (baseline ? "diff" : "document");
  if (params.has("view") && (!baseline || !["left", "diff", "right"].includes(view))) throw new TypeError("Content comparison view is invalid");
  return { selected: selected ?? null, baseline: baseline || null, view, displayed: view === "left" ? baseline : selected ?? null };
}

export function populateContentVersionControls(root, manifest, { selected, baseline, view }) {
  root.querySelector("#content-history-description").innerHTML = renderHistoryDescription(manifest, selected);
  root.querySelector("[data-content-history-revisions]").innerHTML = renderRevisionRows(manifest, selected);
  root.querySelector("[data-content-history-open]").hidden = manifest.revisions.length === 0;
  const comparison = root.querySelector("[data-content-comparison-state]");
  comparison.innerHTML = renderComparisonState(manifest, selected, baseline, view);
  comparison.hidden = !selected || !baseline;
  const download = root.querySelector("[data-content-selected-download]");
  download.hidden = !selected;
  if (selected) download.href = selected.textHref;
  else download.removeAttribute("href");
}

// Legal review policy belongs to history, not to the rich-content diff engine.
export function contentHistoryComparisonOptions(manifest) {
  if (manifest.kind === "editorial") return {};
  return {
    metadataSelector: "[data-legal-document-info]",
    keyAttribute: "data-legal-activity",
    disclaimer: manifest.locale === "de-DE"
      ? "Der Vergleich dient nur der Information und ersetzt weder eine Fassung noch die ausdrückliche Zustimmung. Dokumentangaben werden nicht verglichen."
      : "This comparison is for information only and replaces neither version nor express acceptance. Document metadata is not compared.",
  };
}

export async function readContentResponse(response, type, maxBytes = 1024 * 1024) {
  if (!response.ok || response.headers.get("content-type")?.split(";")[0].trim().toLowerCase() !== type) {
    await response.body?.cancel();
    throw new TypeError("Content response is unavailable or invalid");
  }
  const reader = response.body.getReader();
  const chunks = [];
  let bytes = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      bytes += value.byteLength;
      if (bytes > maxBytes) throw new TypeError("Content response is too large");
      chunks.push(value);
    }
  } finally {
    await reader.cancel();
    reader.releaseLock();
  }
  return new TextDecoder("utf-8", { fatal: true }).decode(await new Blob(chunks).arrayBuffer());
}
